export const DEFAULT_IMAGE = 'assets/images/default.svg'

export enum HTTP_STATUS_CODES {
    NOT_FOUND = 404,
    TOO_MANY_REQUESTS = 429,
    UNAUTHORIZED = 401,
    SESSION_EXPIRED = 403,
    SUSPENDE_USER = 444,
    I_AM_A_TEAPOT = 418,
    UNKNOWN_ERROR = 0,
    SERVER_ERROR = 500,
    CONFLICT = 409,
    BAD_REQUEST = 400,
    GONE = 410,
}

export enum ERROR_REASONS {
    PRODUCT_LIMIT_REACHED = 'PRODUCT_LIMIT_REACHED',
    /** Explorer flow (DEV-11262). The two rejections the UI has to act on.
     *  NOT_ACTIVATED means "finish registering" — it comes back from login,
     *  account recovery, saving a card and changing the email. RESTRICTED means
     *  "save a card first" — from checkout and from subscriptions. */
    EXPLORER_NOT_ACTIVATED = 'EXPLORER_NOT_ACTIVATED',
    EXPLORER_RESTRICTED = 'EXPLORER_RESTRICTED',
}

/**
 * What a form says when it is holding a submit because Turnstile has not handed it a token yet
 * (DEV-11124). The two cases need opposite instructions: while Cloudflare is still deciding,
 * waiting does fix it; once it escalates to an interactive check, waiting never will — only the
 * customer clicking the widget does. Telling an escalated visitor to wait strands them.
 */
export function turnstilePendingMessage(isInteractive: boolean | undefined): string {
    return isInteractive
        ? 'Please complete the verification on this form, then try again.'
        : 'Please wait a moment while we verify your browser, then try again.';
}

/**
 * The `reason` values api_v3_validate_authorization sends, all of them on a 403. These are
 * the only responses that mean the customer has to sign in again.
 *
 * Plain strings on purpose: ERROR_REASONS does not exist on every brand, and the 403
 * constant is called SESSION_EXPIRED here but FORBIDDEN on others. This block has to read
 * the same on all of them.
 */
export const SESSION_ENDED_REASONS: string[] = [
    'SESSION_INVALID',   // signature invalid — every live token the moment the key is rotated
    'SESSION_EXPIRED',   // token past its expiry
    'SESSION_MISSING',   // no Authorization header
];

/**
 * Whether a failed response means the customer has to sign in again.
 *
 * A `reason` is authoritative when the backend sends one. Until it does, only 403 ends the
 * session — that is what the API answers today for a missing or expired token, and what
 * every deployed brand already does, so shipping this ahead of the backend changes nothing.
 *
 * A 401 with no reason is deliberately left alone: it is also how /account/payment-method,
 * /account/delivery-info, /account/subscriptions and /order/tips report a missing field, and
 * how PATCH /account/password reports a wrong current password. Signing someone out over an
 * empty form field would be worse than the problem this fixes.
 */
export const isSessionEndedResponse = (status: number, reason?: string | null): boolean => {
    if (reason) return SESSION_ENDED_REASONS.includes(reason);

    return status === 403;
};
